import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BookOpen, HelpCircle, Check, X } from "lucide-react";

const KNOWLEDGE_FIELDS = [
  ["parking", "Parking", "Where should customers park?"],
  ["payment_methods", "Payment methods", "Cards, cash, insurance, financing…"],
  ["promotions", "Current promotions", "Deals or seasonal offers"],
  ["service_area", "Service area", "Where you operate (for mobile/home services)"],
  ["holiday_hours", "Holiday hours", "Exceptions to your regular hours"],
  ["customer_instructions", "Customer instructions", "Anything customers should know before visiting"],
];

export default function Knowledge() {
  const [b, setB] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [answers, setAnswers] = useState({});

  const load = useCallback(() => {
    api.get("/business").then(r => setB(r.data));
    api.get("/knowledge-gaps").then(r => setGaps(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!b) return <div className="p-8 text-slate-500">Loading…</div>;

  const kn = b.knowledge || {};
  const setKn = (k, v) => setB({ ...b, knowledge: { ...kn, [k]: v } });
  const saveKnowledge = async () => {
    try { await api.patch("/business", { knowledge: kn }); toast.success("Business knowledge saved"); }
    catch { toast.error("Save failed"); }
  };
  const answer = async (gid) => {
    const a = (answers[gid] || "").trim();
    if (!a) return;
    try {
      await api.post(`/knowledge-gaps/${gid}/answer`, { answer: a });
      toast.success("Answer saved — your receptionist will use it on future calls");
      setAnswers({ ...answers, [gid]: "" });
      load();
    } catch { toast.error("Failed"); }
  };
  const dismiss = async (gid) => { await api.delete(`/knowledge-gaps/${gid}`); load(); };

  const open = gaps.filter(g => g.status === "open");
  const answered = gaps.filter(g => g.status === "answered");

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">Business knowledge</h1>
      <p className="text-slate-600 mt-1">Everything your receptionist knows. Answered questions become permanent knowledge.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="knowledge-gaps-section">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-amber-500" />
          <div className="font-heading font-semibold text-lg">Unanswered questions <span className="text-sm font-normal text-slate-500">({open.length})</span></div>
        </div>
        {open.length === 0 ? (
          <p className="text-sm text-slate-500">No open gaps. When your receptionist can't answer something, it shows up here.</p>
        ) : (
          <div className="space-y-4">
            {open.map(g => (
              <div key={g.id} className="p-4 rounded-xl bg-amber-50/60 border border-amber-100" data-testid={`gap-${g.id}`}>
                <div className="text-sm font-medium text-slate-900">"{g.question}"</div>
                <div className="text-xs text-slate-500 mt-1">Asked by a caller · {new Date(g.created_at).toLocaleString()}</div>
                <div className="mt-3 flex gap-2">
                  <Input data-testid={`gap-answer-${g.id}`} placeholder="Type the correct answer…" value={answers[g.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [g.id]: e.target.value })} className="flex-1 bg-white" />
                  <Button data-testid={`gap-approve-${g.id}`} onClick={() => answer(g.id)} className="rounded-xl bg-blue-600 hover:bg-blue-700"><Check className="w-4 h-4" /></Button>
                  <Button data-testid={`gap-dismiss-${g.id}`} variant="outline" onClick={() => dismiss(g.id)} className="rounded-xl"><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {answered.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Learned answers</div>
            {answered.map(g => (
              <div key={g.id} className="py-2 text-sm">
                <span className="font-medium">"{g.question}"</span>
                <span className="text-slate-500"> → {g.answer}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <div className="font-heading font-semibold text-lg">Extra knowledge</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {KNOWLEDGE_FIELDS.map(([k, label, hint]) => (
            <div key={k} className={["customer_instructions", "promotions"].includes(k) ? "sm:col-span-2" : ""}>
              <Label>{label}</Label>
              <Textarea data-testid={`knowledge-${k}`} rows={2} value={kn[k] || ""} onChange={(e) => setKn(k, e.target.value)}
                placeholder={hint} className="mt-1.5" />
            </div>
          ))}
        </div>
        <Button data-testid="knowledge-save-btn" onClick={saveKnowledge} className="mt-5 rounded-xl bg-blue-600 hover:bg-blue-700">Save knowledge</Button>
      </div>
    </div>
  );
}
